import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Camera, Video, Sparkles, User } from "lucide-react";

const ROLE_ICON = {
    Photographer: Camera,
    Videographer: Video,
    Editor: Sparkles,
    Assistant: User,
};

export default function StaffPage() {
    const [staff, setStaff] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: "", contact_number: "", email: "", role: "Photographer", default_rate: 0 });

    const load = async () => {
        try {
            const { data } = await api.get("/staff");
            setStaff(data);
        } catch {
            toast.error("Failed to load staff");
        }
    };

    useEffect(() => {
        load();
    }, []);

    const startCreate = () => {
        setEditing(null);
        setForm({ name: "", contact_number: "", email: "", role: "Photographer", default_rate: 0 });
        setOpen(true);
    };

    const startEdit = (s) => {
        setEditing(s);
        setForm({
            name: s.name,
            contact_number: s.contact_number || "",
            email: s.email || "",
            role: s.role,
            default_rate: s.default_rate,
        });
        setOpen(true);
    };

    const submit = async () => {
        if (!form.name.trim()) return toast.error("Name required");
        try {
            const payload = { ...form, default_rate: Number(form.default_rate) || 0 };
            if (editing) {
                await api.put(`/staff/${editing.id}`, payload);
                toast.success("Staff updated");
            } else {
                await api.post("/staff", payload);
                toast.success("Staff added");
            }
            setOpen(false);
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/staff/${id}`);
            await load();
        } catch {
            toast.error("Delete failed");
        }
    };

    return (
        <div className="p-8 lg:p-10" data-testid="staff-page">
            <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest text-gray-400 mb-2">Team</div>
                    <h1 className="font-serif text-4xl md:text-5xl text-gray-900">Studio Staff</h1>
                    <p className="text-sm text-gray-500 mt-2">
                        {staff.length} member{staff.length !== 1 && "s"} · photographers, videographers & editors
                    </p>
                </div>
                <Button onClick={startCreate} className="bg-blue-500 hover:bg-blue-600" data-testid="add-staff-button">
                    <Plus className="w-4 h-4 mr-1.5" /> Add staff
                </Button>
            </div>

            {staff.length === 0 ? (
                <div className="bg-white border border-dashed border-gray-200 rounded-md p-12 text-center">
                    <h3 className="font-serif text-2xl text-gray-900">No staff yet</h3>
                    <p className="text-sm text-gray-500 mt-2">
                        Add your first photographer, videographer or freelancer to start assigning them to events.
                    </p>
                    <Button onClick={startCreate} className="bg-blue-500 hover:bg-blue-600 mt-6">
                        <Plus className="w-4 h-4 mr-1.5" /> Add staff
                    </Button>
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-md divide-y divide-gray-100" data-testid="staff-list">
                    {staff.map((s) => {
                        const Icon = ROLE_ICON[s.role] || User;
                        return (
                            <div key={s.id} className="flex items-center justify-between px-5 py-4" data-testid={`staff-row-${s.id}`}>
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600">
                                        <Icon className="w-5 h-5" strokeWidth={1.8} />
                                    </div>
                                    <div>
                                        <div className="text-sm font-medium text-gray-900">{s.name}</div>
                                        <div className="text-xs text-gray-500">
                                            {s.role}
                                            {s.contact_number ? ` · ${s.contact_number}` : ""}
                                            {s.email ? ` · ${s.email}` : ""}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="text-right">
                                        <div className="text-[10px] uppercase tracking-widest text-gray-400">Rate</div>
                                        <div className="font-serif text-lg tabular-nums text-gray-900">
                                            Rs {(s.default_rate || 0).toLocaleString()}
                                        </div>
                                    </div>
                                    <button onClick={() => startEdit(s)} className="p-2 text-gray-400 hover:text-gray-900" data-testid={`edit-staff-${s.id}`}>
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => remove(s.id)} className="p-2 text-gray-400 hover:text-red-600" data-testid={`delete-staff-${s.id}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">{editing ? "Edit staff" : "Add staff"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Name</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="staff-form-name" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Phone</Label>
                                <Input value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} className="mt-1" data-testid="staff-form-phone" />
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="staff-form-email" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Role</Label>
                                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                    <SelectTrigger className="mt-1" data-testid="staff-form-role"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Photographer">Photographer</SelectItem>
                                        <SelectItem value="Videographer">Videographer</SelectItem>
                                        <SelectItem value="Editor">Editor</SelectItem>
                                        <SelectItem value="Assistant">Assistant</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Default rate (Rs)</Label>
                                <Input type="number" min={0} value={form.default_rate} onChange={(e) => setForm({ ...form, default_rate: e.target.value })} className="mt-1 tabular-nums" data-testid="staff-form-rate" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={submit} className="bg-blue-500 hover:bg-blue-600" data-testid="staff-form-save">
                            {editing ? "Save changes" : "Add staff"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
